export default {
  id: 'submitting-trade-plan',
  title: 'Submitting a Trade Plan',
  content: `## Submitting a Trade Plan

The trade plan is the core of every ACT session. It is how TradeLab measures whether you are making disciplined, well-structured decisions — not just lucky guesses.

---

## The Trade Plan Fields

### Decision

Choose one before filling in the plan:

| Option | Use when |
|---|---|
| **Enter** | The setup meets your criteria and you would take the trade |
| **Wait** | You need one more confirmation before entering |
| **Skip** | The setup does not meet your criteria |

---

### Entry Price

The price at which you would enter the trade.

**Good entry:** Near the signal price or at a natural support/resistance level visible on the chart.
**Bad entry:** Far above the signal price — chasing a move that has already happened.

Your entry price relative to the signal price affects your **Entry Quality** score.

---

### Stop Loss

The price at which you would exit if the trade goes wrong.

Rules for a good stop loss:
- Below a meaningful support level (not an arbitrary round number)
- At least **1× ATR** away from your entry to avoid being stopped out by normal noise
- Creates a R:R of at least 2:1 with your target

> Leaving Stop Loss blank scores zero on the Risk Management dimension. Always set a stop.

---

### Target

The price at which you will take profit.

- Should be at least 2× the distance of your stop from entry (2:1 R:R minimum)
- Best placed near a known resistance level, not an arbitrary number
- For swing trades, the default ACT target is +8% from entry

---

### Quantity

How many shares you would buy.

TradeLab shows a suggested quantity calculated from your Risk Profile. You can change it, but plans that imply risk above your stated Risk Per Trade % are penalised in scoring.

ACT sessions use a **₹25,000 fixed base** for suggestion calculations across all scenarios regardless of stock price.

---

### Conviction Level

How confident you are in this setup:

| Level | Use when |
|---|---|
| LOW | Setup is marginal — you are taking it but with reservations |
| MEDIUM | Setup is solid — standard confidence |
| HIGH | Setup is excellent — all signals aligned, clear thesis |

Your conviction rating is tracked over time and compared against actual outcomes. If your HIGH conviction trades perform no better than LOW conviction ones, your calibration needs work.

---

### Reason Tags

Select tags that explain why you made this decision. For Enter decisions:
- Strong trend, Volume confirmed, RSI supportive, Breakout of key level, MACD crossover

For Wait or Skip decisions:
- FOMO risk, Low volume, Against trend, No clear stop level, Overextended, Indecision candle, Sector weakness

These tags accumulate over time and reveal your behavioral patterns in the Analytics section.

---

## Submitting

Once all fields are complete, tap **Submit Plan**. The outcome candles are then revealed immediately.

You cannot change your plan after submitting. The irreversibility is intentional — it mirrors real trading where you cannot revise your plan after seeing the outcome.`,
};
